import { useQuery } from "@tanstack/react-query";
import { apiFetch } from "./client";

export interface Recording {
  id: string;
  title: string;
  recorded_at: string | null;
  original_filename: string;
  s3_key_audio: string | null;
  duration_seconds: number | null;
  format: string | null;
  size_bytes: number | null;
  status: "uploaded" | "submitted" | "transcribing" | "transcribed" | "failed";
  asr_provider: string | null;
  asr_job_id: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

export interface TranscriptVersionMeta {
  id: string;
  version: number;
  kind: "raw_assembly" | "corrected" | "summary" | "combined";
  produced_by: string;
  visibility: "private" | "family";
  pdf_s3_key: string | null;
  created_at: string;
}

export interface TranscriptSegment {
  idx: number;
  speaker_label: string;
  start_ms: number;
  end_ms: number;
  text: string;
  confidence: number | null;
}

export interface RecordingSpeaker {
  speaker_label: string;
  person_name: string;
  confirmed_by: string | null;
}

export interface RecordingDetail {
  recording: Recording;
  versions: TranscriptVersionMeta[];
  segments: TranscriptSegment[];
  speakers: RecordingSpeaker[];
}

export function useRecordings() {
  return useQuery<{ items: Recording[] }>({
    queryKey: ["transcription-recordings"],
    queryFn: () => apiFetch<{ items: Recording[] }>("/api/dev/transcription/recordings"),
    refetchInterval: 30_000,
  });
}

export function useRecordingDetail(id: string | null) {
  return useQuery<RecordingDetail>({
    queryKey: ["transcription-recording-detail", id],
    queryFn: () => apiFetch<RecordingDetail>(`/api/dev/transcription/recordings/${id}`),
    enabled: !!id,
  });
}

export function fetchVersionBody(
  recordingId: string,
  version: number
): Promise<{ kind: string; body_text: string }> {
  return apiFetch<{ kind: string; body_text: string }>(
    `/api/dev/transcription/recordings/${recordingId}/versions/${version}`
  );
}

export function useVersionBody(recordingId: string, version: number | null) {
  return useQuery<{ kind: string; body_text: string }>({
    queryKey: ["transcription-version-body", recordingId, version],
    queryFn: () => fetchVersionBody(recordingId, version!),
    enabled: version != null,
    staleTime: 5 * 60_000,
  });
}

export function patchVersionVisibility(
  recordingId: string,
  version: number,
  visibility: "private" | "family"
): Promise<{ ok: boolean }> {
  return apiFetch<{ ok: boolean }>(
    `/api/dev/transcription/recordings/${recordingId}/versions/${version}`,
    { method: "PATCH", body: JSON.stringify({ visibility }) }
  );
}

export function fetchVersionPdfUrl(
  recordingId: string,
  version: number
): Promise<{ url: string }> {
  return apiFetch<{ url: string }>(
    `/api/dev/transcription/recordings/${recordingId}/versions/${version}/pdf-url`
  );
}

export function fetchAudioUrl(recordingId: string): Promise<{ url: string }> {
  return apiFetch<{ url: string }>(
    `/api/dev/transcription/recordings/${recordingId}/audio-url`
  );
}

export interface UploadRow {
  id: string;
  title: string;
  status: Recording["status"];
  duration_seconds: number | null;
  uploaded_by: string | null;
  created_at: string;
  processed: boolean;
  shared: boolean;
}

export function useMyUploads() {
  return useQuery<{ items: UploadRow[] }>({
    queryKey: ["transcription-my-uploads"],
    queryFn: () => apiFetch<{ items: UploadRow[] }>("/api/dev/transcription/my-uploads"),
    refetchInterval: 30_000,
  });
}

export function requestUpload(
  filename: string,
  contentType: string
): Promise<{ s3_key: string; put_url: string }> {
  return apiFetch<{ s3_key: string; put_url: string }>(
    "/api/dev/transcription/uploads",
    { method: "POST", body: JSON.stringify({ filename, content_type: contentType }) }
  );
}

// Direct browser→S3 PUT with progress; bytes never touch the server.
export function putToS3(
  putUrl: string,
  file: File,
  onProgress: (pct: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", putUrl);
    xhr.setRequestHeader("content-type", file.type || "application/octet-stream");
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new Error(`S3 upload failed: ${xhr.status}`));
    xhr.onerror = () => reject(new Error("S3 upload network error"));
    xhr.send(file);
  });
}

export function createRecordingApi(input: {
  title: string;
  s3_key_audio: string;
  original_filename: string;
  format?: string;
  size_bytes?: number;
}): Promise<{ ok: boolean; recording: Recording }> {
  return apiFetch<{ ok: boolean; recording: Recording }>(
    "/api/dev/transcription/recordings",
    { method: "POST", body: JSON.stringify(input) }
  );
}

export function submitRecordingApi(
  id: string
): Promise<{ ok: boolean; recording: Recording }> {
  return apiFetch<{ ok: boolean; recording: Recording }>(
    `/api/dev/transcription/recordings/${id}/submit`,
    { method: "POST" }
  );
}
